/**
 * Captura de la oportunidad de instalación que ofrece el navegador.
 *
 * Chromium dispara `beforeinstallprompt` una sola vez y muy temprano — en la práctica
 * mientras el usuario aún está en la pantalla de login, antes de que exista el encabezado.
 * Por eso el listener se registra al importar este módulo (desde `main.tsx`, antes de
 * renderizar React) y no dentro de un componente: un `useEffect` llegaría tarde y perdería
 * el evento, dejando la instalación nativa inalcanzable de forma intermitente.
 */

/** El evento `beforeinstallprompt` no está tipado en lib.dom.d.ts. */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt: () => Promise<void>;
}

const CLAVE_INSTALADA = 'pwaInstalada:v1';

let eventoPendiente: BeforeInstallPromptEvent | null = null;
let instalacionConocida = leerMarca();
const suscriptores = new Set<() => void>();

function leerMarca(): boolean {
  try {
    return localStorage.getItem(CLAVE_INSTALADA) === '1';
  } catch {
    return false; // almacenamiento no disponible: degradación aceptable
  }
}

function escribirMarca(): void {
  try {
    localStorage.setItem(CLAVE_INSTALADA, '1');
  } catch {
    /* almacenamiento no disponible: degradación aceptable */
  }
}

function notificar(): void {
  suscriptores.forEach((avisar) => avisar());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (evento) => {
    // Sin preventDefault, Chrome muestra además su propia mini-barra de instalación.
    evento.preventDefault();
    eventoPendiente = evento as BeforeInstallPromptEvent;
    notificar();
  });

  window.addEventListener('appinstalled', () => {
    eventoPendiente = null;
    instalacionConocida = true;
    escribirMarca();
    notificar();
  });
}

/** true si la app corre dentro de su propia ventana (ya instalada, por definición). */
export function corriendoComoApp(): boolean {
  if (typeof window === 'undefined') return false;
  const enVentanaPropia = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // Safari en iOS no implementa esa media query; expone un flag propio no estándar.
  const enVentanaPropiaIOS =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return enVentanaPropia || enVentanaPropiaIOS;
}

/** true si el navegador tiene una oportunidad de instalación nativa sin usar. */
export function hayInstalacionNativa(): boolean {
  return eventoPendiente !== null;
}

/**
 * true si consta que la app está instalada en este equipo.
 * En iOS siempre es false: la instalación ocurre en el menú de Safari, fuera del
 * alcance de la app, y no existe API que la reporte. Es una laguna conocida y aceptada.
 */
export function constaInstalada(): boolean {
  return corriendoComoApp() || instalacionConocida;
}

export function suscribir(alCambiar: () => void): () => void {
  suscriptores.add(alCambiar);
  return () => {
    suscriptores.delete(alCambiar);
  };
}

/**
 * Abre el diálogo de instalación del navegador.
 * Devuelve false si no hay oportunidad nativa disponible — el llamador muestra
 * entonces los pasos manuales. El evento se consume: una oportunidad, un uso.
 */
export async function abrirDialogoNativo(): Promise<boolean> {
  const evento = eventoPendiente;
  if (!evento) return false;
  eventoPendiente = null;
  notificar();
  await evento.prompt();
  return true;
}
